import React from "react";
import { Alert } from "@mui/material";
import type { AlertColor, SxProps, Theme } from "@mui/material";
import { useLocalization } from "@fluent/react";

export type StatusLevelAlertStatus = "ERROR" | "WARNING" | "INFO" | "NONE" | "SERVER_ERROR";
export type StatusLevelAlertLevel = 0 | 1 | 2 | 3 | 4;
export type StatusLevelAlertType = "CS" | "DEVICE";

export interface StatusLevelAlertProps {
    status: StatusLevelAlertStatus | null | undefined;
    level: StatusLevelAlertLevel | number | null | undefined;
    type?: StatusLevelAlertType;
    sx?: SxProps<Theme>;
}

const STATUS_TO_SEVERITY: Record<Exclude<StatusLevelAlertStatus, "NONE">, AlertColor> = {
    ERROR: "error",
    WARNING: "warning",
    INFO: "info",
    SERVER_ERROR: "error",
};

const VALID_LEVELS: StatusLevelAlertLevel[] = [0, 1, 2, 3, 4];

export const StatusLevelAlert = ({
    status,
    level,
    type = "DEVICE",
    sx,
}: StatusLevelAlertProps) => {
    const { l10n } = useLocalization();

    const normalizedStatus = (status ?? "NONE").toUpperCase() as StatusLevelAlertStatus;

    const DEVICE_LEVEL_CONTENT: Record<StatusLevelAlertLevel, React.ReactNode> = {
        0: l10n.getString("status-level-alert-device-level-0", null, "_Błędne dane"),
        1: l10n.getString("status-level-alert-device-level-1", null, "_Ten numer seryjny jest już zarejestrowany w firmie"),
        2: l10n.getString("status-level-alert-device-level-2", null, "_Ten numer seryjny jest już powiązany z tym dostawcą"),
        3: l10n.getString("status-level-alert-device-level-3", null, "_Ten numer seryjny jest już przypisany do tej struktury"),
        4: l10n.getString("status-level-alert-device-level-4", null, "_Ten numer seryjny jest już używany w tej strukturze dla tego dostawcy")
    };

    const CS_LEVEL_CONTENT: Record<StatusLevelAlertLevel, React.ReactNode> = {
        0: l10n.getString("status-level-alert-cs-level-0", null, "_Błędne dane"),
        1: l10n.getString("status-level-alert-cs-level-1", null, "_Ten identyfikator jest już zarejestrowany w firmie"),
        2: l10n.getString("status-level-alert-cs-level-2", null, "_Ten identyfikator jest już powiązany z tym dostawcą"),
        3: l10n.getString("status-level-alert-cs-level-3", null, "_Ten identyfikator jest już przypisany do tej struktury"),
        4: l10n.getString("status-level-alert-cs-level-4", null, "_Ten identyfikator jest już używany w tej strukturze dla tego dostawcy"),
    };

    if (normalizedStatus === "NONE") {
        return <></>;
    }

    if (!(normalizedStatus in STATUS_TO_SEVERITY)) {
        return null;
    }

    const isValidLevel = typeof level === "number" && VALID_LEVELS.includes(level as StatusLevelAlertLevel);
    const normalizedLevel = isValidLevel ? (level as StatusLevelAlertLevel) : null;

    const content =
        normalizedStatus === "SERVER_ERROR" ? "Server error" : normalizedLevel === null
            ? "Nieznany poziom."
            : (type === "CS" ? CS_LEVEL_CONTENT : DEVICE_LEVEL_CONTENT)[normalizedLevel];

    return (
        <Alert severity={STATUS_TO_SEVERITY[normalizedStatus]} sx={sx}>
            {content}
        </Alert>
    );
};
